/**
 * EIGOO Auth Loop-Breaker · Director directive 2026.02 (Strike #17)
 *
 * Bookkeeping helpers for `ProtectedRoute`. When a user keeps getting
 * bounced between `/login` and any auth-gated route in under 30s, we
 * intercept the redirect and surface the Sovereign Diagnostic page
 * instead of letting them spin forever in a tab.
 *
 * Implementation choices (deliberate, not over-engineered):
 *  • sessionStorage — survives reloads inside the tab but doesn't
 *    pollute other tabs / persist across sessions.
 *  • Rolling 30s window — tighter windows miss slow loops, wider
 *    windows surface false positives when a user comes back hours
 *    later with a stale cookie.
 *  • Threshold = 3 bounces (i.e. the 3rd bounce trips the breaker).
 *    Two bounces is a normal "I mistyped my password" pattern; three
 *    is a loop.
 */

const KEY = "eigoo:auth_bounces_v1";
export const LOOP_WINDOW_MS = 30_000;
export const LOOP_THRESHOLD = 3;

function readBounces() {
    try {
        const raw = sessionStorage.getItem(KEY);
        if (!raw) return [];
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr.filter((n) => typeof n === "number") : [];
    } catch {
        return [];
    }
}

function writeBounces(arr) {
    try {
        sessionStorage.setItem(KEY, JSON.stringify(arr));
    } catch {
        /* sessionStorage unavailable (private mode / SSR) — silent no-op */
    }
}

/** Record a bounce → /login event. Returns the new bounce count
 *  inside the rolling LOOP_WINDOW_MS. Duplicate calls within 250ms
 *  (e.g. React StrictMode double-render in dev) are collapsed into
 *  a single record so production and dev trip on the same number of
 *  user-visible bounces. */
export function recordAuthBounce() {
    const now = Date.now();
    const recent = readBounces().filter((t) => now - t < LOOP_WINDOW_MS);
    if (recent.length && now - recent[recent.length - 1] < 250) {
        return recent.length;
    }
    recent.push(now);
    writeBounces(recent);
    return recent.length;
}

/** Returns true once the breaker has tripped within the window. */
export function isLoopTripped() {
    const now = Date.now();
    const recent = readBounces().filter((t) => now - t < LOOP_WINDOW_MS);
    return recent.length >= LOOP_THRESHOLD;
}

/** Wipe the counter — called after a successful authenticated render. */
export function resetAuthBounces() {
    try {
        sessionStorage.removeItem(KEY);
    } catch {
        /* no-op */
    }
}
